/**
 * PhaseCompanyDocs — documentos a nivel empresa.
 *
 * Acta, Asambleas y Poder son MULTI-archivo (whitelist en backend);
 * CSF y Comprobante son single-file con avisos de vigencia.
 *
 * Reuso de CSF: si el usuario ya subió la CSF en PhaseCompanyData
 * (data.csf_empresa_uploaded), la sección de CSF aquí muestra "Ya
 * cargada ✓ — Reemplazar" en lugar de un dropzone vacío. El backend
 * sigue siendo single-file por (draft, doc_type, subject); una
 * eventual segunda subida sustituye la anterior, así nunca quedan
 * dos blobs duplicados para la misma empresa.
 *
 * Vigencia: los warnings de antigüedad documental se obtienen del
 * backend (single source of truth) — no hardcodear umbrales aquí.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import SubjectDocUploader from "./SubjectDocUploader";
import DocFreshnessWarnings from "./DocFreshnessWarnings";
import useVigenciaRules from "./useVigenciaRules";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PhaseCompanyDocs({ data, draftId, onNext, onBack, loading }) {
  const [warnKey, setWarnKey] = useState(0);
  const { suffixFor } = useVigenciaRules();

  // Has the user already uploaded csf_empresa in the previous phase?
  // We probe ``extraction-status?subject_type=company`` (cheap, no plan
  // check) — if the status entry exists, the doc is on the server.
  const [csfAlreadyUploaded, setCsfAlreadyUploaded] = useState(
    !!data.csf_empresa_uploaded
  );
  useEffect(() => {
    if (!draftId) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/customer-drafts/${draftId}/extraction-status?subject_type=company`,
          { withCredentials: true },
        );
        if (!alive) return;
        const docs = r.data?.documents || {};
        if (docs.csf_empresa) setCsfAlreadyUploaded(true);
      } catch {
        // Non-fatal — fall back to the data flag.
      }
    })();
    return () => { alive = false; };
  }, [draftId, warnKey]);

  // Build the doc list dynamically — drop csf_empresa from the
  // single-file uploader when it's already been uploaded. We surface
  // a separate "Ya cargada" badge with a "Reemplazar" link that
  // toggles back to the regular uploader for that single doc.
  const [showCsfReplace, setShowCsfReplace] = useState(false);
  const docs = [
    { docType: "acta_constitutiva", label: "Acta Constitutiva (y reformas)", required: true },
    { docType: "asambleas",         label: "Asambleas",                       required: false },
    { docType: "poder",             label: "Poder",                            required: false },
    ...((csfAlreadyUploaded && !showCsfReplace)
      ? []
      : [{ docType: "csf_empresa", label: `Constancia de Situación Fiscal${suffixFor("csf_empresa")}`, required: true }]
    ),
    { docType: "comprobante_domicilio_empresa", label: `Comprobante de domicilio${suffixFor("comprobante_domicilio_empresa")}`, required: true },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">Documentos de la Empresa</h2>
        <p className="text-sm text-slate-400">
          Sube los instrumentos jurídicos de la persona moral. Acta, Asambleas y Poder admiten
          múltiples archivos.
        </p>
      </div>

      {/* CSF ya cargada — recordatorio visible con escape de "Reemplazar" */}
      {csfAlreadyUploaded && !showCsfReplace && (
        <div
          className="rounded-lg border border-teal-700/40 bg-teal-950/20 p-3 text-sm flex items-center gap-2"
          data-testid="csf-empresa-already-uploaded"
        >
          <CheckCircle2 className="w-4 h-4 text-teal-400 shrink-0" />
          <span className="text-slate-300 flex-1">
            Constancia de Situación Fiscal ya cargada desde el paso anterior.
          </span>
          <button
            type="button"
            data-testid="csf-empresa-replace-here-btn"
            onClick={() => setShowCsfReplace(true)}
            className="text-xs text-teal-300 hover:text-teal-200 underline underline-offset-4"
          >
            Reemplazar
          </button>
        </div>
      )}

      <SubjectDocUploader
        draftId={draftId}
        subjectType="company"
        subjectDraftId={null}
        onUploaded={() => setWarnKey((k) => k + 1)}
        docs={docs}
      />

      <DocFreshnessWarnings
        draftId={draftId}
        subjectType="company"
        refreshKey={warnKey}
        testIdSuffix="company"
      />

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} disabled={loading}>← Atrás</Button>
        <Button data-testid="company-docs-continue-btn"
                disabled={loading} onClick={() => onNext({})}>
          {loading ? "Guardando…" : "Continuar →"}
        </Button>
      </div>
    </div>
  );
}
